/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasourceAttributes } from '@cloudio-saas/datasource-types';
import FileSaver from 'file-saver';
import { isNotEmpty, shrink } from 'utils/isEmpty';

function oracleType(attr: IoDatasourceAttributes): string {
  switch (attr.attributeType) {
    case 'String':
    case 'EncryptedString':
      return `VARCHAR2(${attr.maxLength})`;
    case 'Integer':
    case 'Decimal':
    case 'Double':
    case 'Percent':
      return 'NUMBER';
    case 'Boolean':
    case 'TF':
    case 'YN':
      return 'CHAR(1)';
    case 'Date':
      return 'DATE';
    case 'DateTime':
      return 'TIMESTAMP(6)';
    case 'Reference':
    case 'Attachment':
      return 'VARCHAR2(40)';
    case 'Email':
    case 'Phone':
    case 'URL':
    case 'UserID':
      return 'VARCHAR2(128)';
    case 'Textarea':
      return attr.maxLength ?? 9999 <= 4000
        ? `VARCHAR2(${attr.maxLength})`
        : 'CLOB';
    case 'JSON':
      return 'CLOB';
    default:
      return `${attr.attributeType}_NOT_SUPPORTED`;
  }
}

export async function downloadPLSQL(
  tableName: string,
  allAttrs: IoDatasourceAttributes[],
  pkAttrs: IoDatasourceAttributes[],
): Promise<void> {
  const attrs = allAttrs
    .filter(
      (attr) => attr.calculatedColumn !== 'Y' && isNotEmpty(attr.columnName),
    )
    .sort((a, b) => (a.columnName ?? '').localeCompare(b.columnName ?? ''));
  tableName = shrink(tableName, 26);
  const text = `SET DEFINE OFF;
CREATE OR REPLACE PACKAGE ${tableName} AS

/** Generated @ Sat Sep 17 14:38:23 PDT 2022 **/

/** Create the following TYPEs in Oracle
DROP TYPE ${tableName}_TAB;
CREATE OR REPLACE TYPE ${tableName}_REC AS OBJECT(
    ${attrs
      .map((attr) => `${attr.columnName?.padEnd(31, ' ')} ${oracleType(attr)}`)
      .join('\n    , ')}
    , CHANGED_ATTRIBUTE              VARCHAR2(30)
    , CHANGED_ATTRIBUTE_OLD_VALUE    VARCHAR2(250)
    , POST_OPERATION                 VARCHAR2(1)      
    , CONSTRUCTOR FUNCTION ${tableName}_REC RETURN SELF AS RESULT);
/

CREATE OR REPLACE TYPE BODY ${tableName}_REC AS CONSTRUCTOR FUNCTION ${tableName}_REC RETURN SELF AS RESULT AS
  BEGIN
    ${attrs
      .map((attr) => `SELF.${attr.columnName?.padEnd(31, ' ')} := NULL;`)
      .join('\n    ')}
    SELF.CHANGED_ATTRIBUTE              := NULL;
    SELF.CHANGED_ATTRIBUTE_OLD_VALUE    := NULL;
    SELF.POST_OPERATION                 := NULL;      
    RETURN;
  END ${tableName}_REC;
END;
/

CREATE OR REPLACE TYPE ${tableName}_TAB AS TABLE OF ${tableName}_REC;

// Bulk Collection & FORALL Sample Code
DECLARE
l_rows ${tableName}_TAB
CURSOR c1 IS SELECT ${tableName}_REC(
        ${attrs
          .map((attr) =>
            attr.calculatedColumn === 'Y'
              ? attr.columnName
              : `x.${attr.columnName}`,
          )
          .join('\n        , ')}
        , NULL // CHANGED_ATTRIBUTE
        , NULL /* CHANGED_ATTRIBUTE_OLD_VALUE
        , NULL) // POST_OPERATION
    FROM ${tableName} x;
BEGIN
  OPEN c1;
  LOOP
    FETCH c1
      BULK COLLECT INTO l_rows
      LIMIT 1000;
    EXIT WHEN l_rows.count = 0;

    -- to the processing here... e.g. post_query(l_rows);

    FORALL i IN 1 .. l_rows.COUNT
        UPDATE ${tableName} x SET
        ${attrs
          .filter((a) => a.calculatedColumn !== 'Y')
          .map((attr) => `x.${attr.columnName} = l_rows(i).${attr.columnName}`)
          .join('\n          , ')}
        WHERE ${pkAttrs
          .map((attr) => `x.${attr.columnName} = l_rows(i).${attr.columnName}`)
          .join('\n            AND ')};
  END LOOP;
END;
*/

/** POST is called by the framework whenever a record is created/updated and/or deleted by the user **/
PROCEDURE POST(
    rows IN OUT ${tableName}_TAB
);

/** VALIDATE_ROW is called by the framework when a UI item/field, that has 'Server validate on Change' property on, is changed by the user.
Note: This procedure will be called as many times as the user changes the value in all such fields.
**/
PROCEDURE VALIDATE_ROW(
    rows IN OUT ${tableName}_TAB
);

END ${tableName};
/

SET DEFINE OFF;
CREATE OR REPLACE PACKAGE BODY ${tableName} AS

/** Generated by @ Sat Sep 17 14:38:23 PDT 2022 **/

/** POST is called by the framework whenever a record is created/updated and/or deleted by the user **/
PROCEDURE POST(
    rows IN OUT ${tableName}_TAB
) AS

    api_error EXCEPTION;
    PRAGMA EXCEPTION_INIT (api_error, -20001);
    l_return_code VARCHAR2(1) := 'S';
    l_msg_data VARCHAR2(4000);
    l_debug_enabled BOOLEAN := io_global.is_debug_enabled;

BEGIN

    IF (l_debug_enabled) THEN
      FOR i IN 1 .. rows.count LOOP
        ${attrs
          .map(
            (attr) =>
              `io_global.log ('${tableName}.POST', '${attr.columnName?.padEnd(
                30,
                ' ',
              )} (' || i || ') = ' || rows(i).${attr.columnName});`,
          )
          .join('\n        ')}
        io_global.log ('${tableName}.POST', 'CHANGED_ATTRIBUTE             (' || i || ') = ' || rows(i).CHANGED_ATTRIBUTE);
        io_global.log ('${tableName}.POST', 'CHANGED_ATTRIBUTE_OLD_VALUE   (' || i || ') = ' || rows(i).CHANGED_ATTRIBUTE_OLD_VALUE);
        io_global.log ('${tableName}.POST', 'POST_OPERATION                (' || i || ') = ' || rows(i).POST_OPERATION);
      END LOOP;
    END IF;

    -- Do your processing here... e.g. calling standard APIs etc.

    FOR i IN 1 .. rows.count LOOP
    IF (rows(i).POST_OPERATION = 'I') THEN
        NULL;
        /** sample insert statement

        INSERT INTO ${tableName}(
            ${attrs
              .filter((a) => a.calculatedColumn !== 'Y')
              .map((attr) => `${attr.columnName}`)
              .join('\n          , ')}
        ) VALUES (
            ${attrs
              .filter((a) => a.calculatedColumn !== 'Y')
              .map((attr) => `rows(i).${attr.columnName}`)
              .join('\n          , ')}
        );

        **/
    ELSIF (rows(i).POST_OPERATION = 'U') THEN
        NULL;
        /** sample update statement

        UPDATE ${tableName} x SET
          ${attrs
            .filter((a) => a.calculatedColumn !== 'Y')
            .map((attr) => `x.${attr.columnName} = rows(i).${attr.columnName}`)
            .join('\n          , ')}
        WHERE ${pkAttrs
          .map((attr) => `x.${attr.columnName} = rows(i).${attr.columnName}`)
          .join('\n          AND ')};

        **/
    ELSIF (rows(i).POST_OPERATION = 'D') THEN
        NULL;
        /** sample delete statement

        DELETE FROM ${tableName} x
        WHERE ${pkAttrs
          .map((attr) => `x.${attr.columnName} = rows(i).${attr.columnName}`)
          .join('\n          AND ')};

        **/
    END IF;
    END LOOP;

    IF (l_return_code <> 'S') THEN
      raise_application_error (-20001, l_msg_data);
    END IF;

    -- Send the updated out parameters back to the client.
    -- At the minimum you have to send LAST_UPDATE_DATE & auto generated sequence values back to the client

    FOR i IN 1 .. rows.count LOOP
      IF (rows(i).POST_OPERATION <> 'D') THEN
        SELECT ${attrs
          .filter((a) => a.outParam === 'Y')
          .map((attr) =>
            attr.calculatedColumn === 'Y'
              ? attr.columnName
              : `x.${attr.columnName}`,
          )
          .join(', ')}
            INTO ${attrs
              .filter((a) => a.outParam === 'Y')
              .map((attr) => `rows(i).${attr.columnName}`)
              .join(', ')}
        FROM ${tableName} x
        WHERE ${pkAttrs
          .map((attr) => `x.${attr.columnName} = rows(i).${attr.columnName}`)
          .join('\n        AND ')};
      END IF;
    END LOOP;

  EXCEPTION
    WHEN api_error THEN
        RAISE;
    WHEN OTHERS THEN
        io_global.error ('${tableName}.POST', 'Exception Others:' || SQLERRM);
        raise_application_error (-20001, SQLERRM);
END POST;

/** VALIDATE_ROW is called by the framework when a UI item/field, that has 'Server validate on Change' property on, is changed by the user.
Note: This procedure will be called as many times as the user changes the value in all such fields.
**/
PROCEDURE VALIDATE_ROW(
    rows IN OUT ${tableName}_TAB
) AS

    api_error EXCEPTION;
    PRAGMA EXCEPTION_INIT (api_error, -20001);
    l_return_code VARCHAR2(1) := 'S';
    l_msg_data VARCHAR2(4000);
    l_debug_enabled BOOLEAN := io_global.is_debug_enabled;

BEGIN

    IF (l_debug_enabled) THEN
      FOR i IN 1 .. rows.count LOOP
        ${attrs
          .map(
            (attr) =>
              `io_global.log ('${tableName}.POST', '${attr.columnName?.padEnd(
                30,
                ' ',
              )} (' || i || ') = ' || rows(i).${attr.columnName});`,
          )
          .join('\n        ')}
        io_global.log('${tableName}.VALIDATE_ROW', 'CHANGED_ATTRIBUTE             (' || i || ') = ' || rows(i).CHANGED_ATTRIBUTE);
        io_global.log('${tableName}.VALIDATE_ROW', 'CHANGED_ATTRIBUTE_OLD_VALUE   (' || i || ') = ' || rows(i).CHANGED_ATTRIBUTE_OLD_VALUE);
      END LOOP;
    END IF;


    -- Perform your bussiness validation here


    IF (l_return_code <> 'S') THEN
      raise_application_error (-20001, l_msg_data);
    END IF;

  EXCEPTION
    WHEN api_error THEN
        RAISE;
    WHEN OTHERS THEN
        io_global.error ('${tableName}.VALIDATE_ROW', 'Exception Others:' || SQLERRM);
        raise_application_error (-20001, SQLERRM);
END VALIDATE_ROW;

END ${tableName};
/
`;

  const blob = new Blob([text], {
    type: 'application/json;charset=utf-8',
  });
  FileSaver.saveAs(blob, `${tableName}.sql`);
}

export async function downloadPLSQLCRUD(
  tableName: string,
  allAttrs: IoDatasourceAttributes[],
  pkAttrs: IoDatasourceAttributes[],
): Promise<void> {
  const attrs = allAttrs
    .filter(
      (attr) => attr.calculatedColumn !== 'Y' && isNotEmpty(attr.columnName),
    )
    .sort((a, b) => (a.columnName ?? '').localeCompare(b.columnName ?? ''));
  tableName = shrink(tableName, 26);
  const text = `SET DEFINE OFF;
CREATE OR REPLACE PACKAGE ${tableName} AS

/** Generated @ Sat Sep 17 14:38:23 PDT 2022 **/

/** INSERT_ROW is called by the framework whenever a new record is created by the user **/
PROCEDURE INSERT_ROW(
  ${attrs
    .map((attr) =>
      attr.paramName
        ? `${attr.paramName.padEnd(31, ' ')}`
        : `P_${attr.columnName?.padEnd(31, ' ')} IN ${oracleType(attr)}`,
    )
    .join('\n    , ')}
);

/** UPDATE_ROW is called by the framework when an existing record is updated by the user **/
PROCEDURE UPDATE_ROW(
  ${attrs
    .map((attr) =>
      attr.paramName
        ? `${attr.paramName.padEnd(31, ' ')}`
        : `P_${attr.columnName?.padEnd(31, ' ')} IN ${oracleType(attr)}`,
    )
    .join('\n    , ')}
);

/** DELETE_ROW is called by the framework when an existing record is deleted by the user **/
PROCEDURE DELETE_ROW(
  ${pkAttrs
    .map((attr) =>
      attr.paramName
        ? `${attr.paramName.padEnd(31, ' ')}`
        : `P_${attr.columnName?.padEnd(31, ' ')} IN ${oracleType(attr)}`,
    )
    .join('\n    , ')}
);

/** VALIDATE_ROW is called by the framework when a UI item/field, that has 'Server validate on Change' property on, is changed by the user.
Note: This procedure will be called as many times as the user changes the value in all such fields.
**/
PROCEDURE VALIDATE_ROW(
  ${attrs
    .map((attr) =>
      attr.paramName
        ? `${attr.paramName.padEnd(31, ' ')}`
        : `P_${attr.columnName?.padEnd(31, ' ')} IN OUT ${oracleType(attr)}`,
    )
    .join('\n    , ')}
  , P_CHANGED_ATTRIBUTE            IN OUT VARCHAR2
  , P_CHANGED_ATTRIBUTE_OLD_VALUE  IN OUT VARCHAR2
);

END ${tableName};
/

SET DEFINE OFF;
CREATE OR REPLACE PACKAGE BODY ${tableName} AS

/** Generated by @ Sat Sep 17 14:38:23 PDT 2022 **/

/** INSERT_ROW is called by the framework whenever a new record is created by the user **/
PROCEDURE INSERT_ROW(
  ${attrs
    .map((attr) =>
      attr.paramName
        ? `${attr.paramName.padEnd(31, ' ')}`
        : `P_${attr.columnName?.padEnd(31, ' ')} IN ${oracleType(attr)}`,
    )
    .join('\n    , ')}
) AS

    api_error EXCEPTION;
    PRAGMA EXCEPTION_INIT (api_error, -20001);
    l_return_code VARCHAR2(1) := 'S';
    l_msg_data VARCHAR2(4000);
    l_debug_enabled BOOLEAN := io_global.is_debug_enabled;

BEGIN

    IF (l_debug_enabled) THEN
      FOR i IN 1 .. rows.count LOOP
        ${attrs
          .map(
            (attr) =>
              `io_global.log ('${tableName}.INSERT_ROW', '${
                attr.paramName
                  ? `${attr.paramName.padEnd(30, ' ')}`
                  : `P_${attr.columnName?.padEnd(30, ' ')}`
              }  (' || i || ') = ' || rows(i).${
                attr.paramName ? attr.paramName : `P_${attr.columnName}`
              });`,
          )
          .join('\n        ')}
      END LOOP;
    END IF;


    -- Do your processing here... e.g. calling standard APIs etc.

/** sample insert statement
    INSERT INTO ${tableName}(
 ${attrs.map((attr) => `${attr.columnName}}`).join('\n    , ')}
    ) VALUES (
      ${attrs
        .map((attr) =>
          attr.paramName
            ? `${attr.paramName.padEnd(31, ' ')}`
            : `P_${attr.columnName?.padEnd(31, ' ')} ${oracleType(attr)}`,
        )
        .join('\n    , ')}
    );
**/

    IF (l_return_code <> 'S') THEN
      raise_application_error (-20001, l_msg_data);
    END IF;

  EXCEPTION
    WHEN api_error THEN
        RAISE;
    WHEN OTHERS THEN
        io_global.error ('${tableName}.INSERT_ROW', 'Exception Others:' || SQLERRM);
        raise_application_error (-20001, SQLERRM);
END INSERT_ROW;

/** UPDATE_ROW is called by the framework when an existing record is updated by the user **/
PROCEDURE UPDATE_ROW(
  ${attrs
    .map((attr) =>
      attr.paramName
        ? `${attr.paramName.padEnd(31, ' ')}`
        : `P_${attr.columnName?.padEnd(31, ' ')} IN ${oracleType(attr)}`,
    )
    .join('\n    , ')}
) AS

    api_error EXCEPTION;
    PRAGMA EXCEPTION_INIT (api_error, -20001);
    l_return_code VARCHAR2(1) := 'S';
    l_msg_data VARCHAR2(4000);
    l_debug_enabled BOOLEAN := io_global.is_debug_enabled;

BEGIN

    IF (l_debug_enabled) THEN
      FOR i IN 1 .. rows.count LOOP
        ${attrs
          .map(
            (attr) =>
              `io_global.log ('${tableName}.UPDATE_ROW', '${
                attr.paramName
                  ? `${attr.paramName.padEnd(30, ' ')}`
                  : `P_${attr.columnName?.padEnd(30, ' ')}`
              } (' || i || ') = ' || rows(i).${
                attr.paramName ? attr.paramName : `P_${attr.columnName}`
              });`,
          )
          .join('\n        ')}
      END LOOP;
    END IF;


    -- Do your processing here... e.g. calling standard APIs etc.

/** sample update statement
     UPDATE ${tableName} X SET 
     ${attrs
       .map(
         (attr) =>
           `${attr.columnName} = ${
             attr.paramName ? attr.paramName : `P_${attr.columnName}`
           } }`,
       )
       .join('\n    , ')}
       
     WHERE ${pkAttrs
       .map(
         (attr) =>
           `${attr.columnName} = ${
             attr.paramName ? attr.paramName : `P_${attr.columnName}`
           }}`,
       )
       .join('\n    , ')};
 
 **/

    IF (l_return_code <> 'S') THEN
      raise_application_error (-20001, l_msg_data);
    END IF;

  EXCEPTION
    WHEN api_error THEN
        RAISE;
    WHEN OTHERS THEN
        io_global.error ('${tableName}.UPDATE_ROW', 'Exception Others:' || SQLERRM);
        raise_application_error (-20001, SQLERRM);
END UPDATE_ROW;

/** DELETE_ROW is called by the framework when an existing record is deleted by the user **/
PROCEDURE DELETE_ROW(
  ${pkAttrs
    .map(
      (attr) => `${attr.columnName?.padEnd(31, ' ')}  IN ${oracleType(attr)}`,
    )
    .join('\n    , ')}
) AS

    api_error EXCEPTION;
    PRAGMA EXCEPTION_INIT (api_error, -20001);
    l_return_code VARCHAR2(1) := 'S';
    l_msg_data VARCHAR2(4000);
    l_debug_enabled BOOLEAN := io_global.is_debug_enabled;

BEGIN

    IF (l_debug_enabled) THEN
      FOR i IN 1 .. rows.count LOOP
        ${pkAttrs
          .map(
            (attr) =>
              `io_global.log ('${tableName}.DELETE_ROW', '${
                attr.paramName
                  ? `${attr.paramName.padEnd(30, ' ')}`
                  : `P_${attr.columnName?.padEnd(30, ' ')}`
              }(' || i || ') = ' || rows(i).${
                attr.paramName ? attr.paramName : `P_${attr.columnName}`
              });`,
          )
          .join('\n        ')}
      END LOOP;
    END IF;


    -- Do your processing here... e.g. calling standard APIs etc.

/** sample delete statement
     DELETE FROM ${tableName} X 
     WHERE ${pkAttrs
       .map(
         (attr) =>
           `${attr.columnName} = ${
             attr.paramName ? attr.paramName : `P_${attr.columnName}`
           }}`,
       )
       .join('\n    , ')};
 
 **/

    IF (l_return_code <> 'S') THEN
      raise_application_error (-20001, l_msg_data);
    END IF;

  EXCEPTION
    WHEN api_error THEN
        RAISE;
    WHEN OTHERS THEN
        io_global.error ('${tableName}.DELETE_ROW', 'Exception Others:' || SQLERRM);
        raise_application_error (-20001, SQLERRM);
END DELETE_ROW;

/** VALIDATE_ROW is called by the framework when a UI item/field, that has 'Server validate on Change' property on, is changed by the user.
Note: This procedure will be called as many times as the user changes the value in all such fields.
**/
PROCEDURE VALIDATE_ROW(
  ${attrs
    .map(
      (attr) =>
        `${attr.columnName?.padEnd(31, ' ')}  IN OUT ${oracleType(attr)}`,
    )
    .join('\n    , ')}
    , P_CHANGED_ATTRIBUTE            IN OUT VARCHAR2
    , P_CHANGED_ATTRIBUTE_OLD_VALUE  IN OUT VARCHAR2
) AS

    api_error EXCEPTION;
    PRAGMA EXCEPTION_INIT (api_error, -20001);
    l_return_code VARCHAR2(1) := 'S';
    l_msg_data VARCHAR2(4000);
    l_debug_enabled BOOLEAN := io_global.is_debug_enabled;

BEGIN

    IF (l_debug_enabled) THEN
      FOR i IN 1 .. rows.count LOOP
        ${attrs
          .map(
            (attr) =>
              `io_global.log ('${tableName}.VALIDATE_ROW', '${
                attr.paramName
                  ? attr.paramName.padEnd(30, ' ')
                  : `P_${attr.columnName?.padEnd(30, ' ')}`
              } (' || i || ') = ' || rows(i).${
                attr.paramName
                  ? attr.paramName.padEnd(30, ' ')
                  : `P_${attr.columnName?.padEnd(30, ' ')}`
              });`,
          )
          .join('\n        ')}
        io_global.log('${tableName}.VALIDATE_ROW', 'P_CHANGED_ATTRIBUTE             (' || i || ') = ' || rows(i).P_CHANGED_ATTRIBUTE);
        io_global.log('${tableName}.VALIDATE_ROW', 'P_CHANGED_ATTRIBUTE_OLD_VALUE   (' || i || ') = ' || rows(i).P_CHANGED_ATTRIBUTE_OLD_VALUE);
      END LOOP;
    END IF;


    -- Perform your bussiness validation here


    IF (l_return_code <> 'S') THEN
      raise_application_error (-20001, l_msg_data);
    END IF;

  EXCEPTION
    WHEN api_error THEN
        RAISE;
    WHEN OTHERS THEN
        io_global.error ('${tableName}.VALIDATE_ROW', 'Exception Others:' || SQLERRM);
        raise_application_error (-20001, SQLERRM);
END VALIDATE_ROW;

END ${tableName};
/
`;

  const blob = new Blob([text], {
    type: 'application/json;charset=utf-8',
  });
  FileSaver.saveAs(blob, `${tableName}.sql`);
}
